import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Alert, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField,
  MenuItem, Tooltip, FormControlLabel, Checkbox, Divider,
} from '@mui/material';
import { Link, LinkOff, CloudDownload, Settings, Save, Business, InfoOutlined, Schedule, Sync } from '@mui/icons-material';
import {
  getZohoCredentials, saveZohoCredentials,
  getZohoStatus, getZohoAuthUrl, disconnectZoho,
  listZohoOrganizations, setZohoOrganization,
  pullZohoStatuses, getZohoSyncStatus,
  updateZohoAutoSync,
  previewZohoPull, runZohoPull,
  listZohoAccounts, setZohoDefaultIncomeAccount,
} from '../api';

const FALLBACK_DOMAINS = ['.com', '.eu', '.in', '.com.au', '.jp', '.com.cn'];

export default function ZohoSettings({ embedded = false }) {
  const [credentials, setCredentials] = useState({ clientId: '', clientSecret: '', redirectUri: '', zohoDomain: '.com' });
  const [supportedDomains, setSupportedDomains] = useState(FALLBACK_DOMAINS);
  const [credsSaved, setCredsSaved] = useState(false);
  const [connection, setConnection] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [savingOrg, setSavingOrg] = useState(false);
  const [syncStatus, setSyncStatus] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [disconnectDialog, setDisconnectDialog] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [autoSync, setAutoSync] = useState({
    autoPush: false,
    autoSync: false,
    autoSyncIntervalMinutes: 60,
  });
  const [savingAutoSync, setSavingAutoSync] = useState(false);
  const [pullOptions, setPullOptions] = useState({
    customers: true,
    contactPersons: true,
    addresses: true,
    paymentTerms: true,
    customFields: false,
    orgDefaults: true,
    chartOfAccounts: false,
  });
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pullLoading, setPullLoading] = useState(false);
  const [pullResult, setPullResult] = useState(null);
  const [incomeAccounts, setIncomeAccounts] = useState([]);
  const [selectedIncomeAccount, setSelectedIncomeAccount] = useState('');
  const [savingIncomeAccount, setSavingIncomeAccount] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const [credsRes, connRes, syncRes] = await Promise.all([
        getZohoCredentials(),
        getZohoStatus(),
        getZohoSyncStatus().catch(() => ({ data: [] })),
      ]);
      if (credsRes.data.supportedDomains) setSupportedDomains(credsRes.data.supportedDomains);
      if (credsRes.data.configured) {
        setCredentials({
          clientId: credsRes.data.clientId || '',
          clientSecret: '',
          redirectUri: credsRes.data.redirectUri || '',
          zohoDomain: credsRes.data.zohoDomain || credsRes.data.defaultDomain || '.com',
        });
        setCredsSaved(true);
      } else {
        setCredentials(prev => ({
          ...prev,
          zohoDomain: credsRes.data.defaultDomain || prev.zohoDomain || '.com',
        }));
      }
      setConnection(connRes.data);
      setSyncStatus(syncRes.data);
      if (connRes.data) {
        setAutoSync({
          autoPush: !!connRes.data.autoPush,
          autoSync: !!connRes.data.autoSync,
          autoSyncIntervalMinutes: connRes.data.autoSyncIntervalMinutes || 60,
        });
      }
    } catch (err) {
      setError('Failed to load Zoho status');
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Load org list whenever we're connected but no org is bound yet,
  // so the user can pick one.
  useEffect(() => {
    if (!connection?.connected) { setOrgs([]); return; }
    if (connection.organizationId) return;
    setOrgsLoading(true);
    listZohoOrganizations()
      .then(res => {
        setOrgs(res.data.organizations || []);
        if (res.data.organizations?.length === 1) {
          setSelectedOrg(res.data.organizations[0].organizationId);
        }
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load Zoho organizations'))
      .finally(() => setOrgsLoading(false));
  }, [connection?.connected, connection?.organizationId]);

  // Surface OAuth callback result. The callback now lands the user inside
  // their tenant (`/<slug>/?zohoConnected=true|zohoError=...`); the older
  // `connected=true|error=...` form is kept so any in-flight callbacks during
  // the deploy still surface a message.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('zohoConnected') === 'true' || params.get('connected') === 'true';
    const errMsg = params.get('zohoError') || params.get('error');

    if (connected) {
      setSuccess('Successfully connected to Zoho Books');
      loadStatus();
    }
    if (errMsg) {
      setError(`Zoho connection failed: ${errMsg}`);
    }

    if (connected || errMsg) {
      // Strip the params so a refresh doesn't re-fire the toast.
      const url = new URL(window.location.href);
      ['zohoConnected', 'zohoError', 'connected', 'error', 'tab'].forEach(k => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.toString());
    }
  }, [loadStatus]);

  const handleSaveCredentials = async () => {
    if (!credentials.clientId || !credentials.clientSecret || !credentials.redirectUri) {
      setError('Client ID, Client Secret, and Redirect URI are required');
      return;
    }
    setSavingCreds(true);
    setError('');
    try {
      await saveZohoCredentials(credentials);
      setSuccess('Zoho API credentials saved');
      setCredsSaved(true);
      setShowCredentials(false);
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save credentials');
    } finally {
      setSavingCreds(false);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await getZohoAuthUrl();
      window.location.href = res.data.authUrl;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start Zoho connection');
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await disconnectZoho();
      setDisconnectDialog(false);
      setSuccess('Disconnected from Zoho Books');
      loadStatus();
    } catch (err) {
      setError('Failed to disconnect from Zoho');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrg = async () => {
    if (!selectedOrg) return;
    setSavingOrg(true);
    try {
      const res = await setZohoOrganization(selectedOrg);
      setSuccess(`Bound to Zoho organization "${res.data.name}"`);
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set organization');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleSaveAutoSync = async (next) => {
    // Optimistic update — the checkbox flips immediately, revert on failure.
    const prev = autoSync;
    setAutoSync(next);
    setSavingAutoSync(true);
    try {
      const res = await updateZohoAutoSync(next);
      setAutoSync({
        autoPush: !!res.data.autoPush,
        autoSync: !!res.data.autoSync,
        autoSyncIntervalMinutes: res.data.autoSyncIntervalMinutes || 60,
      });
      setSuccess('Auto-sync settings saved');
    } catch (err) {
      setAutoSync(prev);
      setError(err.response?.data?.error || 'Failed to save auto-sync settings');
    } finally {
      setSavingAutoSync(false);
    }
  };

  const handlePreviewPull = async () => {
    setPreviewLoading(true);
    setPreviewData(null);
    setError('');
    try {
      const res = await previewZohoPull(pullOptions);
      setPreviewData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to preview pull');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRunPull = async () => {
    setPullLoading(true);
    setPullResult(null);
    setError('');
    try {
      const res = await runZohoPull(pullOptions);
      setPullResult(res.data);
      setPreviewData(null);
      setSuccess('Pull from Zoho complete');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to run pull');
    } finally {
      setPullLoading(false);
    }
  };

  const handleLoadAccounts = useCallback(async () => {
    try {
      const res = await listZohoAccounts();
      setIncomeAccounts(res.data.accounts || []);
    } catch (err) {
      // Silent — only matters if user tries to pick an account
    }
  }, []);

  useEffect(() => {
    if (connection?.connected && connection?.organizationId && pullOptions.chartOfAccounts) {
      handleLoadAccounts();
    }
  }, [connection?.connected, connection?.organizationId, pullOptions.chartOfAccounts, handleLoadAccounts]);

  const handleSaveIncomeAccount = async () => {
    setSavingIncomeAccount(true);
    try {
      await setZohoDefaultIncomeAccount(selectedIncomeAccount || null);
      setSuccess('Default income account saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save default income account');
    } finally {
      setSavingIncomeAccount(false);
    }
  };

  const togglePullOption = (key) => (e) => {
    setPullOptions(prev => {
      const next = { ...prev, [key]: e.target.checked };
      // Sub-options only make sense when `customers` is on; keep them in a
      // coherent state so the UI never asks Zoho for contact_persons without
      // the customer pull that carries them.
      if (key === 'customers' && !e.target.checked) {
        next.contactPersons = false;
        next.addresses = false;
        next.paymentTerms = false;
        next.customFields = false;
      }
      return next;
    });
    setPreviewData(null);
  };

  const handlePullStatuses = async () => {
    setLoading(true);
    try {
      const res = await pullZohoStatuses();
      setSuccess(`Sync complete: ${res.data.checked} checked, ${res.data.updated} updated`);
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync Zoho statuses');
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';
  const orgName = orgs.find(o => o.organizationId === connection?.organizationId)?.name;

  return (
    <Box>
      {!embedded && (
        <Typography variant="h5" sx={{ mb: 3 }}>Zoho Books Integration</Typography>
      )}
      {embedded && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CloudDownload sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Zoho Books Integration</Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* API Credentials */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">API Configuration</Typography>
          <Chip
            icon={<Settings />}
            label={credsSaved ? 'Configured' : 'Not Configured'}
            color={credsSaved ? 'success' : 'default'}
          />
        </Box>

        {!credsSaved || showCredentials ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your Zoho API credentials. Create a Server-based Application at{' '}
              <strong>api-console.zoho.com</strong> to get these values. Pick the Data Center
              that matches where your Zoho account lives.
            </Typography>
            <Stack spacing={2} sx={{ mb: 2 }}>
              <TextField
                select
                label="Data Center"
                value={credentials.zohoDomain}
                onChange={(e) => setCredentials(prev => ({ ...prev, zohoDomain: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText="Use the domain shown in your URL bar at accounts.zoho.* — most US accounts are .com"
              >
                {supportedDomains.map(d => (
                  <MenuItem key={d} value={d}>zoho{d}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Client ID"
                value={credentials.clientId}
                onChange={(e) => setCredentials(prev => ({ ...prev, clientId: e.target.value }))}
                fullWidth
                size="small"
                placeholder="1000.XXXXXXXXXXXXXXXXXXXX"
              />
              <TextField
                label="Client Secret"
                value={credentials.clientSecret}
                onChange={(e) => setCredentials(prev => ({ ...prev, clientSecret: e.target.value }))}
                fullWidth
                size="small"
                type="password"
                placeholder={credsSaved ? '(unchanged — enter new value to update)' : ''}
              />
              <TextField
                label="Redirect URI"
                value={credentials.redirectUri}
                onChange={(e) => setCredentials(prev => ({ ...prev, redirectUri: e.target.value }))}
                fullWidth
                size="small"
                placeholder="https://your-app-url/api/zoho/callback"
                helperText="Must match the Authorized Redirect URI in your Zoho API Console exactly"
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained" startIcon={<Save />}
                onClick={handleSaveCredentials} disabled={savingCreds}>
                {savingCreds ? <CircularProgress size={20} /> : 'Save Credentials'}
              </Button>
              {credsSaved && (
                <Button onClick={() => setShowCredentials(false)}>Cancel</Button>
              )}
            </Stack>
          </>
        ) : (
          <>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Data Center: zoho{credentials.zohoDomain}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Client ID: {credentials.clientId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirect URI: {credentials.redirectUri}
              </Typography>
            </Stack>
            <Button
              variant="outlined" size="small" startIcon={<Settings />}
              onClick={() => setShowCredentials(true)}>
              Update Credentials
            </Button>
          </>
        )}
      </Paper>

      {/* Connection */}
      {credsSaved && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Connection</Typography>
            <Chip
              icon={connection?.connected ? <Link /> : <LinkOff />}
              label={connection?.connected ? 'Connected' : 'Not Connected'}
              color={connection?.connected ? 'success' : 'default'}
            />
          </Box>

          {connection?.connected ? (
            <>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {connection.organizationId && (
                  <Typography variant="body2" color="text.secondary">
                    Organization: {orgName ? `${orgName} (${connection.organizationId})` : connection.organizationId}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Connected: {fmtDate(connection.connectedAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Token expires: {fmtDate(connection.tokenExpires)}
                </Typography>
              </Stack>
              <Button
                variant="outlined" color="error" startIcon={<LinkOff />}
                onClick={() => setDisconnectDialog(true)} disabled={loading}>
                Disconnect
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click below to authorize HourTrax to access your Zoho Books account.
                You'll be redirected to Zoho to grant permission.
              </Typography>
              <Button
                variant="contained" startIcon={<Link />}
                onClick={handleConnect} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Connect to Zoho'}
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* Organization picker — visible after OAuth, before an org is bound */}
      {connection?.connected && !connection?.organizationId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Business sx={{ mr: 1 }} />
            <Typography variant="h6">Select Organization</Typography>
            <Tooltip title="Your Zoho account may have access to multiple organizations. Pick the one this HourTrax tenant should push invoices into.">
              <InfoOutlined fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
            </Tooltip>
          </Box>
          {orgsLoading ? (
            <CircularProgress size={20} />
          ) : orgs.length === 0 ? (
            <Alert severity="warning">No Zoho organizations available on this connection.</Alert>
          ) : (
            <Stack spacing={2}>
              <TextField
                select
                label="Zoho Organization"
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                {orgs.map(o => (
                  <MenuItem key={o.organizationId} value={o.organizationId}>
                    {o.name} {o.isDefault ? '(default)' : ''} — {o.organizationId}
                  </MenuItem>
                ))}
              </TextField>
              <Box>
                <Button
                  variant="contained" startIcon={<Save />}
                  onClick={handleSelectOrg} disabled={!selectedOrg || savingOrg}>
                  {savingOrg ? <CircularProgress size={20} /> : 'Bind Organization'}
                </Button>
              </Box>
            </Stack>
          )}
        </Paper>
      )}

      {/* Sync Actions */}
      {connection?.connected && connection?.organizationId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Sync</Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined" startIcon={<CloudDownload />}
              onClick={handlePullStatuses} disabled={loading}>
              Pull Invoice Statuses
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Push invoices to Zoho from the Invoices page using the "Push to Zoho" action on each row.
          </Typography>
        </Paper>
      )}

      {/* Automation */}
      {connection?.connected && connection?.organizationId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Schedule sx={{ mr: 1 }} />
            <Typography variant="h6">Automation</Typography>
            <Tooltip title="Let HourTrax push new invoices and pull paid/void statuses from Zoho on a schedule, instead of clicking manually for every invoice.">
              <InfoOutlined fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
            </Tooltip>
          </Box>

          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoSync.autoPush}
                  disabled={savingAutoSync}
                  onChange={(e) => handleSaveAutoSync({ ...autoSync, autoPush: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Automatically push new HourTrax invoices to Zoho</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When a new invoice is auto-generated on the tenant's schedule, it's pushed to Zoho Books in the same tick.
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={autoSync.autoSync}
                  disabled={savingAutoSync}
                  onChange={(e) => handleSaveAutoSync({ ...autoSync, autoSync: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Automatically pull invoice statuses from Zoho</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Paid / void status in Zoho flows back to HourTrax so time entries advance through the pipeline without manual pulls.
                  </Typography>
                </Box>
              }
            />

            {autoSync.autoSync && (
              <Box sx={{ pl: 4, pt: 1 }}>
                <TextField
                  label="Sync every"
                  type="number"
                  size="small"
                  value={autoSync.autoSyncIntervalMinutes}
                  disabled={savingAutoSync}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 5, max: 1440, step: 5 }}
                  helperText="Minutes between pulls (5–1440). Default 60."
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value, 10);
                    if (Number.isFinite(minutes)) {
                      setAutoSync(prev => ({ ...prev, autoSyncIntervalMinutes: minutes }));
                    }
                  }}
                  onBlur={() => handleSaveAutoSync(autoSync)}
                  sx={{ width: 200 }}
                />
              </Box>
            )}

            {connection?.lastSyncAt && (
              <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
                Last auto-sync: {fmtDate(connection.lastSyncAt)}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {/* Configurable Pull from Zoho */}
      {connection?.connected && connection?.organizationId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Sync sx={{ mr: 1 }} />
            <Typography variant="h6">Sync from Zoho</Typography>
            <Tooltip title="Pick the data you want to mirror from Zoho Books into HourTrax. Useful for onboarding: skip retyping customers, contacts, and org defaults. Preview first to see exactly what would change.">
              <InfoOutlined fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
            </Tooltip>
          </Box>

          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={pullOptions.customers}
                  onChange={togglePullOption('customers')}
                />
              }
              label="Customers (companies)"
            />
            <Box sx={{ pl: 4 }}>
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={pullOptions.contactPersons}
                      disabled={!pullOptions.customers}
                      onChange={togglePullOption('contactPersons')}
                    />
                  }
                  label={<Typography variant="body2">Include contact persons (billing / AP contacts inside each customer)</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={pullOptions.addresses}
                      disabled={!pullOptions.customers}
                      onChange={togglePullOption('addresses')}
                    />
                  }
                  label={<Typography variant="body2">Include billing & shipping addresses</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={pullOptions.paymentTerms}
                      disabled={!pullOptions.customers}
                      onChange={togglePullOption('paymentTerms')}
                    />
                  }
                  label={<Typography variant="body2">Include per-customer payment terms (overrides tenant default)</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={pullOptions.customFields}
                      disabled={!pullOptions.customers}
                      onChange={togglePullOption('customFields')}
                    />
                  }
                  label={<Typography variant="body2">Include custom fields on customer records (e.g. PO #, project code)</Typography>}
                />
              </Stack>
            </Box>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={pullOptions.orgDefaults}
                  onChange={togglePullOption('orgDefaults')}
                />
              }
              label="Organization defaults (payment terms, currency, address)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={pullOptions.chartOfAccounts}
                  onChange={togglePullOption('chartOfAccounts')}
                />
              }
              label="Chart of Accounts — income accounts (for invoice categorization)"
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={handlePreviewPull}
              disabled={previewLoading || pullLoading}
              startIcon={previewLoading ? <CircularProgress size={16} /> : null}>
              Preview changes
            </Button>
            <Button
              variant="contained"
              onClick={handleRunPull}
              disabled={!previewData || pullLoading}
              startIcon={pullLoading ? <CircularProgress size={16} /> : <CloudDownload />}>
              Run sync
            </Button>
          </Stack>

          {previewData && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Preview — nothing has changed yet</Typography>
              {previewData.customers && (
                <Typography variant="body2">
                  Customers: {previewData.customers.create} create, {previewData.customers.update} update, {previewData.customers.linkByName} link-by-name (of {previewData.customers.totalInZoho} in Zoho)
                </Typography>
              )}
              {previewData.contactPersons && (
                <Typography variant="body2">
                  Contact persons: {previewData.contactPersons.total} found across all customers
                </Typography>
              )}
              {previewData.orgDefaults && (
                <Typography variant="body2">
                  Org defaults: currency={previewData.orgDefaults.currencyCode || '—'}, payment terms={previewData.orgDefaults.paymentTermsLabel || '—'}
                </Typography>
              )}
              {previewData.chartOfAccounts && (
                <Typography variant="body2">
                  Income accounts: {previewData.chartOfAccounts.active} active / {previewData.chartOfAccounts.total} total
                </Typography>
              )}
              {previewData.customFields && (
                <Typography variant="body2">
                  Custom field definitions: {previewData.customFields.active} active / {previewData.customFields.total} total
                </Typography>
              )}
            </Alert>
          )}

          {pullResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Pull complete</Typography>
              {pullResult.customers && (
                <Typography variant="body2">
                  Customers: +{pullResult.customers.created} / ~{pullResult.customers.updated}
                  {pullResult.customers.errors?.length > 0 && ` (${pullResult.customers.errors.length} errors — see server logs)`}
                </Typography>
              )}
              {pullResult.contactPersons && (
                <Typography variant="body2">
                  Contact persons: +{pullResult.contactPersons.created} / ~{pullResult.contactPersons.updated}
                </Typography>
              )}
              {pullResult.orgDefaults && (
                <Typography variant="body2">
                  Applied org defaults: terms={pullResult.orgDefaults.appliedTerms || '—'}
                </Typography>
              )}
              {pullResult.chartOfAccounts && (
                <Typography variant="body2">
                  Cached {pullResult.chartOfAccounts.total} income accounts (pick one below to categorize pushed invoices)
                </Typography>
              )}
              {pullResult.customFields && (
                <Typography variant="body2">
                  Cached {pullResult.customFields.total} custom field definitions
                </Typography>
              )}
            </Alert>
          )}

          {/* Default Income Account picker — only useful once COA has been pulled */}
          {incomeAccounts.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Default Income Account for pushed invoices
                <Tooltip title="Line items on invoices pushed to Zoho will be recorded against this income account. Leave empty to let Zoho pick (usually 'Sales').">
                  <InfoOutlined fontSize="small" sx={{ ml: 0.5, color: 'text.secondary', verticalAlign: 'middle' }} />
                </Tooltip>
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  size="small"
                  value={selectedIncomeAccount}
                  onChange={(e) => setSelectedIncomeAccount(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 320 }}
                  label="Income Account"
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value=""><em>(Zoho default)</em></MenuItem>
                  {incomeAccounts.filter(a => a.isActive).map(a => (
                    <MenuItem key={a.accountId} value={a.accountId}>
                      {a.accountName}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="outlined"
                  onClick={handleSaveIncomeAccount}
                  disabled={savingIncomeAccount}>
                  {savingIncomeAccount ? <CircularProgress size={18} /> : 'Save'}
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      {/* Sync Status Table */}
      {syncStatus.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Sync Mappings</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Entity Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Count</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Synced</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncStatus.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.entity_type}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.sync_status}
                        color={row.sync_status === 'synced' ? 'success' : row.sync_status === 'error' ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                    <TableCell>{fmtDate(row.last_synced)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={disconnectDialog} onClose={() => setDisconnectDialog(false)}>
        <DialogTitle>Disconnect Zoho Books</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This will deactivate the connection and clear stored tokens. Sync mappings are preserved
            so reconnecting will not duplicate existing invoices in Zoho.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDisconnect} disabled={loading}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
